import React, { useMemo, lazy, Suspense } from "react";
import { motion } from "framer-motion";
import { formatCurrency } from "../../utils/Format";

import GlobalTimelineApex from "../../components/charts/GlobalTimelineApex";

const safe4DDate = (v) => {
	if (!v || v === "0000-00-00") return null;
	const d = new Date(v);
	return Number.isNaN(d.getTime()) ? null : d;
};

const isExpertiseDoc = (doc) => {
	const titre = (doc?.Titre ?? "").toLowerCase();
	return doc?._Type_Document === "Expertise" || titre.startsWith("expertise");
};

const getExpertiseDate = (doc) =>
	safe4DDate(doc?.Date_Convoc) || safe4DDate(doc?.Date_evenement);

const getSiteName = (uuid, sites) => {
	const s = sites?.find((x) => x.UUID_ === uuid);
	return s?.Nom || "Inconnu";
};

const cardVariants = {
	hidden: { opacity: 0, y: 12 },
	show: { opacity: 1, y: 0, transition: { duration: 0.35 } },
};

const sectionVariants = {
	hidden: { opacity: 0, y: 16 },
	show: { opacity: 1, y: 0, transition: { duration: 0.45 } },
};

export default function GeneralTab({
	sites = [],
	declarations = [],
	dommages = [],
	lstDocs = [],
	loadingS = false,
	loadingD = false,
	loadingDo = false,
	loadingL = false,
}) {
	const currentYear = new Date().getFullYear();
	const previousYear = currentYear - 1;

	// ---------------------------
	// KPI COUNTS
	// ---------------------------
	const expertises = useMemo(() => lstDocs.filter(isExpertiseDoc), [lstDocs]);

	const kpi = useMemo(() => {
		const expOpen = expertises.filter((e) => !e.Cloture).length;
		const expClosed = expertises.filter((e) => e.Cloture).length;

		const declaArchived = declarations.filter((d) => d.Archiver_Enr).length;
		const dmgArchived = dommages.filter((d) => d.Archiver_Enr).length;

		const totalSitesCostDecl = sites.reduce((acc, s) => acc + (Number(s.Total_A_Payer) || 0), 0);
		const totalSitesCostPatho = sites.reduce((acc, s) => acc + (Number(s.Total_Pathologie) || 0), 0);

		return {
			sitesCount: sites.length,
			declarationsCount: declarations.length,
			dommagesCount: dommages.length,
			expertisesCount: expertises.length,

			expOpen,
			expClosed,
			declaArchived,
			dmgArchived,

			totalSitesCostDecl: Math.round(totalSitesCostDecl),
			totalSitesCostPatho: Math.round(totalSitesCostPatho),
		};
	}, [sites, declarations, dommages, expertises]);

	const sitesWithCost = sites.filter(
		(s) => (Number(s.Total_A_Payer) || 0) > 0 || (Number(s.Total_Pathologie) || 0) > 0
	).length;

	const totalSitesCost = (kpi.totalSitesCostDecl || 0) + (kpi.totalSitesCostPatho || 0);
	const avgCostPerSite = sites.length ? Math.round(totalSitesCost / sites.length) : 0;
	const pctSitesWithCost = sites.length ? Math.round((sitesWithCost / sites.length) * 100) : 0;

	const totalDeclas = declarations.length;
	const totalDommages = dommages.length;

	const pctDeclasArchived = totalDeclas ? Math.round((kpi.declaArchived / totalDeclas) * 100) : 0;
	const pctDommagesArchived = totalDommages ? Math.round((kpi.dmgArchived / totalDommages) * 100) : 0;

	const expTotal = expertises.length;

	const monthStats = (() => {
		const now = new Date();
		const curY = now.getFullYear();
		const curM = now.getMonth();

		const prevDate = new Date(curY, curM - 1, 1);
		const prevY = prevDate.getFullYear();
		const prevM = prevDate.getMonth();

		const curCount = expertises.reduce((acc, e) => {
			const d = getExpertiseDate(e);
			return d && d.getFullYear() === curY && d.getMonth() === curM ? acc + 1 : acc;
		}, 0);

		const prevCount = expertises.reduce((acc, e) => {
			const d = getExpertiseDate(e);
			return d && d.getFullYear() === prevY && d.getMonth() === prevM ? acc + 1 : acc;
		}, 0);

		const diff = curCount - prevCount;
		const diffPct = prevCount ? Math.round((diff / prevCount) * 100) : (curCount ? 100 : 0);

		return { curCount, prevCount, diff, diffPct };
	})();

	const topCA = (() => {
		const map = {};
		expertises.forEach((e) => {
			const ca = e?.CA || "Inconnu";
			map[ca] = (map[ca] || 0) + 1;
		});

		let best = { name: "—", count: 0 };
		Object.entries(map).forEach(([name, count]) => {
			if (count > best.count) best = { name, count };
		});
		return best;
	})();

	const topSite = (() => {
		const map = {};
		expertises.forEach((e) => {
			const uuid = e?.UUID_Site_ || "INCONNU";
			map[uuid] = (map[uuid] || 0) + 1;
		});

		let best = { uuid: "—", count: 0 };
		Object.entries(map).forEach(([uuid, count]) => {
			if (count > best.count) best = { uuid, count };
		});

		return { name: getSiteName(best.uuid, sites), count: best.count };
	})();

	const convocValidCount = expertises.filter((e) => safe4DDate(e?.Date_Convoc)).length;
	const convocValidPct = expTotal ? Math.round((convocValidCount / expTotal) * 100) : 0;

	const sentCount = expertises.filter((e) => {
		const mailDate = safe4DDate(e?.Mail_Date_Envoi);
		return Boolean(e?.Envoye) || Boolean(mailDate) || Boolean(e?.Mail);
	}).length;
	const sentPct = expTotal ? Math.round((sentCount / expTotal) * 100) : 0;

	const declaYearStats = useMemo(() => {
		const sumByYear = (year) => {
			const rows = declarations.filter((d) => {
				const dd = safe4DDate(d.Date_Declaration) || safe4DDate(d.Date_creation);
				return dd && dd.getFullYear() === year;
			});

			const totalPatho = rows.reduce((acc, d) => acc + (Number(d.Cout_tt_Pathologie) || 0), 0);
			const totalDef = rows.reduce((acc, d) => acc + (Number(d.Montant_Du_Definitif) || 0), 0);

			return {
				totalPatho: Math.round(totalPatho),
				totalDef: Math.round(totalDef),
				count: rows.length,
			};
		};

		return {
			current: sumByYear(currentYear),
			previous: sumByYear(previousYear),
		};
	}, [declarations, currentYear, previousYear]);

	const countSitesByYear = (year) =>
		sites.filter((s) => {
			const d = safe4DDate(s.Date_PVRecep_String);
			return d && d.getFullYear() === year;
		}).length;

	const countDommagesByYear = (year) =>
		dommages.filter((dmg) => {
			const d = safe4DDate(dmg.Date_PVRecep);
			return d && d.getFullYear() === year;
		}).length;
		
	const countExpertisesByYear = (year) =>
		expertises.filter((e) => {
			const d = getExpertiseDate(e);
			return d && d.getFullYear() === year;
		}).length;

	const yearCounts = {
		current: {
			sites: countSitesByYear(currentYear),
			dommages: countDommagesByYear(currentYear),
			expertises: countExpertisesByYear(currentYear),
		},
		previous: {
			sites: countSitesByYear(previousYear),
			dommages: countDommagesByYear(previousYear),
			expertises: countExpertisesByYear(previousYear),
		},
	};
	
	return (
		<>
			<motion.div
				initial="hidden"
				animate="show"
				className="gap-4 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 mt-4 md:mt-8"
			>
				{[
					{ label: "Sites", value: kpi.sitesCount, icon: "fa-city", loading: loadingS },
					{ label: "Déclarations", value: kpi.declarationsCount, icon: "fa-scroll", loading: loadingD },
					{ label: "Dommages", value: kpi.dommagesCount, icon: "fa-fire-extinguisher", loading: loadingDo },
					{ label: "Expertises", value: kpi.expertisesCount, icon: "fa-file-signature", loading: loadingL },
				].map((c) => (
					<motion.div key={c.label} variants={cardVariants} className="bg-white rounded-lg overflow-hidden">
						<div className="bg-gradient-to-tr from-orange-600 via-orange-500 to-orange-400 p-4">
							<div className="flex justify-between items-start">
								<div className="text-white">
									<i className={`fa-solid ${c.icon} fa-lg`} />
								</div>
								<div className={`h-6 w-16 rounded-full ${c.loading ? "bg-white/40 animate-pulse" : "bg-white/20"}`} />
							</div>
							<div className="mt-4 font-bold text-white text-3xl leading-8">
								{c.loading ? <div className="bg-white/40 rounded-md w-20 h-8 animate-pulse" /> : c.value}
							</div>
							<div className="mt-1 text-white/90">{c.label}</div>
						</div>
					</motion.div>
				))}
			</motion.div>

			<motion.div
				variants={sectionVariants}
				initial="hidden"
				animate="show"
				className="gap-4 grid grid-cols-1 lg:grid-cols-3 mt-4 md:mt-8"
			>
				{/* SITES */}
				<div className="relative bg-white p-5 border border-orange-100 rounded-xl overflow-hidden">
					<div className="top-0 absolute inset-x-0 bg-gradient-to-r from-orange-600 via-orange-500 to-orange-400 h-1" />
					<div className="flex justify-between items-start">
						<div>
							<div className="font-bold text-orange-600 text-sm uppercase tracking-wide">
								Sites
							</div>
							<div className="mt-1 text-gray-500 text-xs">
								Synthèse des coûts agrégés
							</div>
						</div>
						<div className="bg-orange-50 px-3 py-1 rounded-full font-semibold text-orange-600 text-xs">
							{sites.length} sites
						</div>
					</div>

					<div className="gap-3 grid grid-cols-1 mt-4">
						<div className="bg-gradient-to-tr from-orange-50 to-white p-4 border border-orange-100 rounded-lg">
							<div className="text-orange-500 text-xs uppercase tracking-widest">Total Déclarations</div>
							<div className="mt-1 font-extrabold text-orange-600 text-2xl">
								{formatCurrency(kpi.totalSitesCostDecl)}
							</div>
						</div>

						<div className="bg-gradient-to-tr from-orange-50 to-white p-4 border border-orange-100 rounded-lg">
							<div className="text-orange-500 text-xs uppercase tracking-widest">Total Pathologies</div>
							<div className="mt-1 font-extrabold text-orange-600 text-2xl">
								{formatCurrency(kpi.totalSitesCostPatho)}
							</div>
						</div>

						<div className="gap-3 grid grid-cols-2">
							<div className="bg-white p-4 border border-orange-100 rounded-lg">
								<div className="text-orange-500 text-xs uppercase tracking-widest">Coût moyen / site</div>
								<div className="mt-1 font-bold text-orange-600 text-xl">
									{formatCurrency(avgCostPerSite)}
								</div>
							</div>

							<div className="bg-white p-4 border border-orange-100 rounded-lg">
								<div className="text-orange-500 text-xs uppercase tracking-widest">Sites avec coûts</div>
								<div className="flex items-end gap-2 mt-1">
									<span className="font-bold text-orange-600 text-xl">{sitesWithCost}</span>
									<span className="font-semibold text-orange-400 text-sm">({pctSitesWithCost}%)</span>
								</div>
							</div>
						</div>
					</div>
				</div>

				{/* ARCHIVAGE */}
				<div className="relative bg-white p-5 border border-orange-100 rounded-xl overflow-hidden">
					<div className="top-0 absolute inset-x-0 bg-gradient-to-r from-orange-600 via-orange-500 to-orange-400 h-1" />
					<div className="flex justify-between items-start">
						<div>
							<div className="font-bold text-orange-600 text-sm uppercase tracking-wide">
								Archivage
							</div>
							<div className="mt-1 text-gray-500 text-xs">
								Volume & proportion archivée
							</div>
						</div>
						<div className="bg-orange-50 px-3 py-1 rounded-full font-semibold text-orange-600 text-xs">
							Vue globale
						</div>
					</div>

					<div className="gap-3 grid grid-cols-1 mt-4">
						<div className="bg-gradient-to-tr from-orange-50 to-white p-4 border border-orange-100 rounded-lg">
							<div className="text-orange-500 text-xs uppercase tracking-widest">Déclarations archivées</div>
							<div className="flex justify-between items-end mt-1">
								<div className="font-extrabold text-orange-600 text-2xl">{kpi.declaArchived}</div>
								<div className="font-semibold text-orange-400 text-sm">{pctDeclasArchived}%</div>
							</div>
							<div className="bg-orange-100 mt-2 rounded-full h-2 overflow-hidden">
								<div className="bg-orange-500 h-full" style={{ width: `${pctDeclasArchived}%` }} />
							</div>
							<div className="mt-2 text-gray-500 text-xs">
								Total : <span className="font-semibold">{totalDeclas}</span>
							</div>
						</div>

						<div className="bg-gradient-to-tr from-orange-50 to-white p-4 border border-orange-100 rounded-lg">
							<div className="text-orange-500 text-xs uppercase tracking-widest">Dommages archivés</div>
							<div className="flex justify-between items-end mt-1">
								<div className="font-extrabold text-orange-600 text-2xl">{kpi.dmgArchived}</div>
								<div className="font-semibold text-orange-400 text-sm">{pctDommagesArchived}%</div>
							</div>
							<div className="bg-orange-100 mt-2 rounded-full h-2 overflow-hidden">
								<div className="bg-orange-500 h-full" style={{ width: `${pctDommagesArchived}%` }} />
							</div>
							<div className="mt-2 text-gray-500 text-xs">
								Total : <span className="font-semibold">{totalDommages}</span>
							</div>
						</div>
					</div>
				</div>

				{/* EXPERTISES */}
				<div className="relative bg-white p-5 border border-orange-100 rounded-xl overflow-hidden">
					<div className="top-0 absolute inset-x-0 bg-gradient-to-r from-orange-600 via-orange-500 to-orange-400 h-1" />

					<div className="flex justify-between items-start">
						<div>
							<div className="font-bold text-orange-600 text-sm uppercase tracking-wide">
								Expertises
							</div>
							<div className="mt-1 text-gray-500 text-xs">
								Activité
							</div>
						</div>

						<div className="bg-orange-50 px-3 py-1 rounded-full font-semibold text-orange-600 text-xs">
							{expTotal} total
						</div>
					</div>

					{/* Ce mois-ci */}
					<div className="bg-gradient-to-tr from-orange-50 to-white mt-4 p-4 border border-orange-100 rounded-lg">
						<div className="flex justify-between items-end">
							<div>
								<div className="text-orange-500 text-xs uppercase tracking-widest">
									Ce mois-ci
								</div>
								<div className="mt-1 font-extrabold text-orange-600 text-2xl">
									{monthStats.curCount}
								</div>
							</div>

							<div className="text-right">
								<div className="text-gray-500 text-xs">Mois précédent</div>
								<div className="font-bold text-orange-600">{monthStats.prevCount}</div>
								<div className={`text-xs font-semibold ${monthStats.diff >= 0 ? "text-green-600" : "text-red-600"}`}>
									{monthStats.diff >= 0 ? "+" : ""}{monthStats.diff} ({monthStats.diffPct}%)
								</div>
							</div>
						</div>
					</div>

					{/* Top CA / Top Site */}
					<div className="gap-3 grid grid-cols-1 sm:grid-cols-2 mt-3">
						<div className="bg-white p-4 border border-orange-100 rounded-lg">
							<div className="text-orange-500 text-xs uppercase tracking-widest">Top CA</div>
							<div className="mt-1 font-bold text-orange-600 truncate">{topCA.name}</div>
							<div className="mt-1 text-gray-500 text-xs">{topCA.count} expertises</div>
						</div>

						<div className="bg-white p-4 border border-orange-100 rounded-lg">
							<div className="text-orange-500 text-xs uppercase tracking-widest">Top Site</div>
							<div className="mt-1 font-bold text-orange-600 truncate">{topSite.name}</div>
							<div className="mt-1 text-gray-500 text-xs">{topSite.count} expertises</div>
						</div>
					</div>

					{/* Qualité des données */}
					{/* <div className="gap-3 grid grid-cols-1 sm:grid-cols-2 mt-3">
						<div className="bg-white p-4 border border-orange-100 rounded-lg">
							<div className="text-orange-500 text-xs uppercase tracking-widest">Convocations valides</div>
							<div className="flex justify-between items-end mt-1">
								<div className="font-bold text-orange-600 text-xl">{convocValidPct}%</div>
								<div className="text-gray-500 text-xs">{convocValidCount}/{expTotal}</div>
							</div>
							<div className="bg-orange-100 mt-2 rounded-full h-2 overflow-hidden">
								<div className="bg-orange-500 h-full" style={{ width: `${convocValidPct}%` }} />
							</div>
						</div>

						<div className="bg-white p-4 border border-orange-100 rounded-lg">
							<div className="text-orange-500 text-xs uppercase tracking-widest">Envoi / Mail</div>
							<div className="flex justify-between items-end mt-1">
								<div className="font-bold text-orange-600 text-xl">{sentPct}%</div>
								<div className="text-gray-500 text-xs">{sentCount}/{expTotal}</div>
							</div>
							<div className="bg-orange-100 mt-2 rounded-full h-2 overflow-hidden">
								<div className="bg-orange-500 h-full" style={{ width: `${sentPct}%` }} />
							</div>
						</div>
					</div> */}
				</div>

			</motion.div>

			{/* COSTS + TRENDS */}
			<motion.div
				variants={sectionVariants}
				initial="hidden"
				animate="show"
				className="mt-4 md:mt-8"
			>
				<div className="gap-4 grid grid-cols-1 lg:grid-cols-2">
					{/* ===== PREVIOUS YEAR ===== */}
					<div className="relative bg-gradient-to-tr from-orange-600 via-orange-500 to-orange-400 shadow-lg p-6 rounded-2xl overflow-hidden text-white">
						{/* petite lumière */}
						<div className="-top-24 -right-24 absolute bg-white/15 blur-2xl rounded-full w-56 h-56 pointer-events-none" />

						<div className="flex justify-between items-center">
							<div>
								<div className="text-white/80 text-xs uppercase tracking-[0.2em]">Résumé</div>
								<div className="mt-1 font-extrabold text-2xl uppercase">Année {previousYear}</div>
							</div>
							<div className="bg-white/15 backdrop-blur px-4 py-2 rounded-full font-semibold text-xs">
								Comparatif
							</div>
						</div>

						<div className="gap-4 grid grid-cols-1 md:grid-cols-2 mt-6">
							{/* Bloc gauche */}
							<div className="space-y-3">
								<div className="bg-white/10 hover:bg-white/15 backdrop-blur p-4 border border-white/15 rounded-xl transition">
									<div className="text-white/80 text-xs uppercase tracking-widest">Sites</div>
									<div className="mt-1 font-extrabold text-3xl">{yearCounts.previous.sites}</div>
								</div>

								<div className="bg-white/10 hover:bg-white/15 backdrop-blur p-4 border border-white/15 rounded-xl transition">
									<div className="text-white/80 text-xs uppercase tracking-widest">Dommages</div>
									<div className="mt-1 font-extrabold text-3xl">{yearCounts.previous.dommages}</div>
								</div>

								<div className="bg-white/10 hover:bg-white/15 backdrop-blur p-4 border border-white/15 rounded-xl transition">
									<div className="text-white/80 text-xs uppercase tracking-widest">Expertises</div>
									<div className="mt-1 font-extrabold text-3xl">{yearCounts.previous.expertises}</div>
								</div>
							</div>

							{/* Bloc droite */}
							<div className="space-y-3">
								<div className="bg-white/10 hover:bg-white/15 backdrop-blur p-4 border border-white/15 rounded-xl transition">
									<div className="text-white/80 text-xs uppercase tracking-widest">Déclarations</div>
									<div className="mt-1 font-extrabold text-3xl">{declaYearStats.previous.count}</div>
								</div>

								<div className="bg-white/10 hover:bg-white/15 backdrop-blur p-4 border border-white/15 rounded-xl transition">
									<div className="text-white/80 text-xs uppercase tracking-widest">Coûts Pathologies</div>
									<div className="mt-1 font-extrabold text-3xl">
										{formatCurrency(declaYearStats.previous.totalPatho)}
									</div>
								</div>

								<div className="bg-white/10 hover:bg-white/15 backdrop-blur p-4 border border-white/15 rounded-xl transition">
									<div className="text-white/80 text-xs uppercase tracking-widest">Coûts Déclarations</div>
									<div className="mt-1 font-extrabold text-3xl">
										{formatCurrency(declaYearStats.previous.totalDef)}
									</div>
								</div>
							</div>
						</div>
					</div>

					{/* ===== CURRENT YEAR ===== */}
					<div className="relative bg-gradient-to-tr from-orange-600 via-orange-500 to-orange-400 shadow-lg p-6 rounded-2xl overflow-hidden text-white">
						<div className="-top-24 -right-24 absolute bg-white/15 blur-2xl rounded-full w-56 h-56 pointer-events-none" />

						<div className="flex justify-between items-center">
							<div>
								<div className="text-white/80 text-xs uppercase tracking-[0.2em]">Résumé</div>
								<div className="mt-1 font-extrabold text-2xl uppercase">Année {currentYear}</div>
							</div>
							<div className="bg-white/15 backdrop-blur px-4 py-2 rounded-full font-semibold text-xs">
								En cours
							</div>
						</div>

						<div className="gap-4 grid grid-cols-1 md:grid-cols-2 mt-6">
							{/* Bloc gauche */}
							<div className="space-y-3">
								<div className="bg-white/10 hover:bg-white/15 backdrop-blur p-4 border border-white/15 rounded-xl transition">
									<div className="text-white/80 text-xs uppercase tracking-widest">Sites</div>
									<div className="mt-1 font-extrabold text-3xl">{yearCounts.current.sites}</div>
								</div>

								<div className="bg-white/10 hover:bg-white/15 backdrop-blur p-4 border border-white/15 rounded-xl transition">
									<div className="text-white/80 text-xs uppercase tracking-widest">Dommages</div>
									<div className="mt-1 font-extrabold text-3xl">{yearCounts.current.dommages}</div>
								</div>

								<div className="bg-white/10 hover:bg-white/15 backdrop-blur p-4 border border-white/15 rounded-xl transition">
									<div className="text-white/80 text-xs uppercase tracking-widest">Expertises</div>
									<div className="mt-1 font-extrabold text-3xl">{yearCounts.current.expertises}</div>
								</div>
							</div>

							{/* Bloc droite */}
							<div className="space-y-3">
								<div className="bg-white/10 hover:bg-white/15 backdrop-blur p-4 border border-white/15 rounded-xl transition">
									<div className="text-white/80 text-xs uppercase tracking-widest">Déclarations</div>
									<div className="mt-1 font-extrabold text-3xl">{declaYearStats.current.count}</div>
								</div>

								<div className="bg-white/10 hover:bg-white/15 backdrop-blur p-4 border border-white/15 rounded-xl transition">
									<div className="text-white/80 text-xs uppercase tracking-widest">Coûts Pathologies</div>
									<div className="mt-1 font-extrabold text-3xl">
										{formatCurrency(declaYearStats.current.totalPatho)}
									</div>
								</div>

								<div className="bg-white/10 hover:bg-white/15 backdrop-blur p-4 border border-white/15 rounded-xl transition">
									<div className="text-white/80 text-xs uppercase tracking-widest">Coûts Déclarations</div>
									<div className="mt-1 font-extrabold text-3xl">
										{formatCurrency(declaYearStats.current.totalDef)}
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</motion.div>
						
			<div class="shadow-lg mt-8 rounded-2xl">
				<Suspense fallback={<div className="flex justify-center items-center h-64">Chargement du graphique...</div>}>
					<GlobalTimelineApex
						sites={sites}
						declarations={declarations}
						dommages={dommages}
						expertises={expertises}
					/>
				</Suspense>
			</div>

		</>
	);
}
